//
// # SaveAsDialog
//
// Modal dialog for saving the current design under a new name at an
// arbitrary-depth path, preserving the contentKind from the active design.
// The user types the full design path as a single slash-separated string
// (e.g. "library/cell/name"); it is stored verbatim. The type is carried by
// contentKind, not a path segment.
//
// ## Destinations
//
// The destination is the Depot or an OpenAccess library, which covers OA
// import and export. The two are different address spaces: a depot ident is
// one path, an OA cellview a (lib, cell, view) whose view name is asked for
// rather than derived. `Editor/scripts/oa-export.py` does the same from a
// terminal.
//

import React, { useCallback, useEffect, useState } from "react";

import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { extractErrorMessage } from "../../errorUtil";
import { Ident } from "GenAlphaModel";
import { CommandPaletteDialog } from "./CommandPaletteDialog";
import {
  buildDesignPath,
  formatPath,
  isValidPathInput,
  isValidSegment,
  pathClashes,
  saveAsTargetIdent,
} from "./pathInput";
import {
  DestinationChoice,
  OaAddressFields,
  OA_ADDRESS_FIELDS,
  TechCheckNote,
  oaAddressValid,
  useOaTechCheck,
  type OaAddress,
} from "./OaDestination";
import {
  PathField,
  PATH_FORMAT_ERROR,
  SEGMENT_FORMAT_ERROR,
} from "./PathField";

export interface SaveAsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function SaveAsDialog({ open, onClose }: SaveAsDialogProps) {
  const workspace = useWorkspace();
  const { activeIdent, activeDesignInfo } = useWorkspaceSnapshot();

  // Form state. `pathText` is the slash-separated *parent* path; `nameText` is
  // the leaf name entered separately. The full design path is the parent
  // segments plus the leaf. Both are pre-filled by splitting the active
  // design's path (parent = all but last, leaf = last), so recombining
  // reproduces the current path.
  const [pathText, setPathText] = useState("");
  const [nameText, setNameText] = useState("");

  // The OpenAccess destination. `oaView` is not derived from the content kind
  // (e.g. a layout view may be named `layout_full_place_route`).
  const [toOpenAccess, setToOpenAccess] = useState(false);
  const [oaLib, setOaLib] = useState("");
  const [oaView, setOaView] = useState("");

  // Existing full design-path strings (path.join("/")), used to block clashes
  // with content already in the depot — Save As must not silently overwrite an
  // existing design (which could also replace it with a different contentKind).
  const [existingPaths, setExistingPaths] = useState<ReadonlySet<string>>(
    new Set()
  );

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation state (shown after first submit attempt or on blur)
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Determine if the active design is from OpenAccess
  const isOpenAccess = activeIdent?.kind === "libcellview";

  // Get content kind display name
  const contentKind = activeDesignInfo?.contentKind ?? "design";

  // Reset form when dialog opens, pre-filling with current design info. Split
  // the current path into parent (all but last) + leaf (last) so recombining
  // reproduces it.
  useEffect(() => {
    if (open && activeIdent) {
      if (activeIdent.kind === "hierpath") {
        const path = activeIdent.path;
        setPathText(formatPath(path.slice(0, -1)));
        setNameText(path[path.length - 1] ?? "");
      } else if (activeIdent.kind === "libcellview") {
        // OpenAccess ident: lib is the parent path, cell is the leaf name.
        setPathText(formatPath([activeIdent.lib]));
        setNameText(activeIdent.cell);
      } else {
        setPathText("");
        setNameText("");
      }
      // An OA design pre-fills its own library and view. A depot design leaves
      // the library blank, since a wrong guess would create one.
      if (activeIdent.kind === "libcellview") {
        setOaLib(activeIdent.lib);
        setOaView(activeIdent.view);
      } else {
        setOaLib("");
        setOaView(activeDesignInfo?.contentKind ?? "layout");
      }
      setToOpenAccess(false);
      setError(null);
      setTouched({});
      setSaving(false);
    }
  }, [open, activeIdent]);

  // Load the set of existing full design paths once so we can block clashes
  // with content already in the depot as the user types.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void workspace.listContent().then((result) => {
      if (cancelled || result.err) return;
      setExistingPaths(new Set(result.val.map((id) => id.path.join("/"))));
    });
    return () => {
      cancelled = true;
    };
  }, [open, workspace]);

  const markTouched = useCallback((field: string) => {
    setTouched((t) => ({ ...t, [field]: true }));
  }, []);

  // Validation. A parent path is required (no bare top-level leaves), as is a
  // valid leaf name.
  const pathValid = isValidPathInput(pathText);
  const nameValid = isValidSegment(nameText);

  // Full design path = parent segments + leaf name.
  const fullSegments = buildDesignPath(pathText, nameText);

  // Block clashes with existing depot content. One design per path, so any
  // existing design at this path clashes regardless of type. This also blocks
  // saving back onto the source design's own path — Save As needs a new path.
  //
  // Depot only: for OpenAccess the server refuses an overwrite without the
  // revision the edit was made against.
  const clashes =
    !toOpenAccess &&
    pathValid &&
    nameValid &&
    pathClashes(existingPaths, fullSegments);

  // The OA cell is the same state as the depot leaf name, so switching
  // destination keeps it.
  const oa: OaAddress = { lib: oaLib, cell: nameText, view: oaView };
  const setOa = useCallback((next: OaAddress) => {
    setOaLib(next.lib);
    setNameText(next.cell);
    setOaView(next.view);
  }, []);

  // A warning, not a gate; see `OaDestination`.
  const techCheck = useOaTechCheck(toOpenAccess, oaLib);

  const canSubmit = toOpenAccess
    ? oaAddressValid(oa) && !saving
    : pathValid && nameValid && !clashes && !saving;

  const handleSubmit = useCallback(async () => {
    // Mark all fields as touched to show validation errors
    setTouched({
      path: true,
      name: true,
      ...Object.fromEntries(OA_ADDRESS_FIELDS.map((f) => [f, true])),
    });

    if (!canSubmit) return;

    setSaving(true);
    setError(null);

    // For OpenAccess only the leaf (the cell) is sent.
    const result = await workspace.saveAs(
      toOpenAccess ? [nameText] : buildDesignPath(pathText, nameText),
      toOpenAccess
        ? { kind: "openaccess", lib: oaLib, view: oaView }
        : { kind: "depot" }
    );

    if (result.err) {
      setError(`Failed to save: ${extractErrorMessage(result.val)}`);
      setSaving(false);
      return;
    }

    // Success - close the dialog
    onClose();
  }, [
    canSubmit,
    pathText,
    nameText,
    toOpenAccess,
    oaLib,
    oaView,
    workspace,
    onClose,
  ]);

  // Get display name for the current design
  const currentDesignName = activeIdent
    ? Ident.displayName(activeIdent)
    : "No active design";

  // Built by the same function the save uses, so the preview matches the
  // write. Only shown once the fields validate.
  const previewIdent = saveAsTargetIdent(
    toOpenAccess ? [nameText] : fullSegments,
    toOpenAccess
      ? { kind: "openaccess", lib: oaLib, view: oaView }
      : { kind: "depot" }
  );

  // Depot leaf name; the clash (full-path dependent) is surfaced here. For
  // OpenAccess, `OaAddressFields` renders the same value as the cell.
  const leafField = (
    <PathField
      label="Name"
      placeholder="name"
      value={nameText}
      onChange={setNameText}
      onBlur={() => {
        markTouched("name");
      }}
      invalid={touched.name && !(nameValid && !clashes)}
      message={
        touched.name && !nameValid
          ? { text: SEGMENT_FORMAT_ERROR, isError: true }
          : clashes
          ? {
              text: `A design already exists at ${formatPath(fullSegments)}.`,
              isError: true,
            }
          : {
              text: `Leaf name; the saved copy stays a ${contentKind}.`,
              isError: false,
            }
      }
    />
  );

  if (!open) return null;

  return (
    <CommandPaletteDialog
      title="Save As"
      hint="Enter to save · Escape to cancel"
      submitLabel={saving ? "Saving..." : "Save"}
      canSubmit={canSubmit}
      onSubmit={() => void handleSubmit()}
      onClose={onClose}
    >
      {/* Current design info */}
      <div className="add-instance-param-field">
        <label className="add-instance-param-label">Current Design</label>
        <div
          className="add-instance-param-input"
          style={{ color: "var(--color-ide-text-dim)" }}
        >
          {currentDesignName}
        </div>
      </div>

      {/* Content type badge */}
      <div className="add-instance-param-field flex items-center gap-2">
        <span className="add-instance-param-label" style={{ margin: 0 }}>
          Type:
        </span>
        <span
          className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase"
          style={{
            backgroundColor: "var(--color-ide-accent-dim)",
            color: "var(--color-ide-accent)",
          }}
        >
          {contentKind}
        </span>
        {isOpenAccess && (
          <span
            className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase"
            style={{
              backgroundColor: "rgba(255, 193, 7, 0.15)",
              color: "#ffc107",
            }}
          >
            OpenAccess
          </span>
        )}
      </div>

      {/* Destination: the Depot, or an OpenAccess library. */}
      <DestinationChoice
        label="Save to"
        name="save-as-destination"
        toOpenAccess={toOpenAccess}
        onChange={setToOpenAccess}
      />

      {isOpenAccess && !toOpenAccess && (
        <div
          className="add-instance-param-constraint"
          style={{ fontStyle: "normal" }}
        >
          This design is from OpenAccess. It will be saved to the Depot at the
          specified path.
        </div>
      )}

      {toOpenAccess && (
        <div
          className="add-instance-param-constraint"
          style={{ fontStyle: "normal" }}
        >
          The library is created if it does not exist. Its technology comes from
          the design&apos;s own metal stack, so a design that names none cannot
          be saved here.
        </div>
      )}

      {/* Technology check for the named library. */}
      {toOpenAccess && <TechCheckNote check={techCheck} />}

      {/* Parent path — depot only. */}
      {!toOpenAccess && (
        <PathField
          label="Path"
          placeholder="library/cell"
          value={pathText}
          onChange={setPathText}
          onBlur={() => {
            markTouched("path");
          }}
          invalid={touched.path && !pathValid}
          message={
            touched.path && !pathValid
              ? { text: PATH_FORMAT_ERROR, isError: true }
              : { text: "Parent path, e.g. library/cell.", isError: false }
          }
          autoFocus
        />
      )}

      {/* OpenAccess library, cell and view; see `oa` above. */}
      {toOpenAccess && (
        <OaAddressFields
          address={oa}
          onChange={setOa}
          touched={touched}
          markTouched={markTouched}
          cellHint={`OpenAccess cell name; the saved copy stays a ${contentKind}.`}
        />
      )}

      {!toOpenAccess && leafField}

      {/* Where this will land; see `previewIdent`. */}
      {canSubmit && (
        <div className="add-instance-param-field">
          <label className="add-instance-param-label">Saving to</label>
          <div
            className="add-instance-param-input"
            style={{ color: "var(--color-ide-text-dim)" }}
          >
            {Ident.displayName(previewIdent)}
          </div>
          <span className="add-instance-param-constraint">
            {toOpenAccess
              ? "library / cell / view, in an OpenAccess workspace on the gateway."
              : "The full design path in the Depot."}
          </span>
        </div>
      )}

      {/* Error banner */}
      {error && <span className="add-instance-param-error">{error}</span>}
    </CommandPaletteDialog>
  );
}
